import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import LoadingSpinner from "../../components/LoadingSpinner";
import { listAllClients } from "../../lib/automatic";
import { getErrorMessage } from "../../lib/utility";
import type { Client } from "@platform/dmv-api";



/**
 * Entry page for the Client Config Comparison tool.
 *
 * Loads all platform clients (via admin API) and presents a searchable
 * dropdown. Selecting a client navigates to /internal/clients/:id/compare.
 */
const ClientSearch = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Partial<Client>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<Partial<Client> | null>(
    null
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await listAllClients();
        // Sort by displayName for easy scanning
        const sorted = [...(data ?? [])].sort((a, b) =>
          (a.displayName ?? "").localeCompare(b.displayName ?? "")
        );
        setClients(sorted);
      } catch (err) {
        setError(getErrorMessage(err, "Failed to load clients"));
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        e.target instanceof Node &&
        !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = clients.filter((c) => {
    const q = query.toLowerCase();
    return (
      c.displayName?.toLowerCase().includes(q) ||
      c.id?.toLowerCase().includes(q) ||
      c.entity?.toLowerCase().includes(q)
    );
  });

  const handleSelect = (client: Partial<Client>) => {
    setSelected(client);
    setQuery(client.displayName ?? "");
    setIsOpen(false);
  };

  const handleGo = () => {
    if (selected) {
      navigate(`/internal/clients/${selected.id}/compare`);
    }
  };

  return (
    <div className="flex flex-col flex-1 h-full px-12 gap-y-8 py-10">
      <div>
        <h1 className="text-2xl font-bold">
          Platform and Destination Client Config Comparison
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Select a platform client to compare configs across its destination
          clients.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-x-3 text-sm text-gray-500">
          <LoadingSpinner />
          <span>Loading clients...</span>
        </div>
      ) : error ? (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3">
          {error}
        </div>
      ) : (
        <div className="flex flex-col gap-y-4 max-w-lg">
          <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Platform client
            </label>
            <input
              ref={inputRef}
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              placeholder="Search by name, ID, or entity..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelected(null);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
            />

            {isOpen && filtered.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto">
                {filtered.map((client) => (
                  <li
                    key={client.id}
                    className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 flex items-center justify-between gap-x-4"
                    onMouseDown={() => handleSelect(client)}
                  >
                    <span className="font-medium truncate">
                      {client.displayName}
                    </span>
                    <span className="text-xs text-gray-400 shrink-0">
                      {client.id}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {isOpen && query && filtered.length === 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow px-3 py-2 text-sm text-gray-500">
                No clients match &quot;{query}&quot;
              </div>
            )}
          </div>

          <button
            onClick={handleGo}
            disabled={!selected}
            className="w-fit px-4 py-2 bg-black text-white text-sm font-medium rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors"
          >
            View destination clients &rarr;
          </button>

          {clients.length > 0 && (
            <p className="text-xs text-gray-400">
              {clients.length} platform clients loaded
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientSearch;
